import React from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../../atoms'
import {
  SearchBarContainer,
  SearchInput,
  SearchControls,
  SearchButton,
  SearchCounter,
  CloseButton,
} from './PDFSearchBar.styles'

export interface PDFSearchBarProps {
  searchQuery: string
  currentMatchIndex: number
  totalMatches: number
  onSearchChange: (query: string) => void
  onNextMatch: () => void
  onPreviousMatch: () => void
  onClose: () => void
}

export const PDFSearchBar: React.FC<PDFSearchBarProps> = ({
  searchQuery,
  currentMatchIndex,
  totalMatches,
  onSearchChange,
  onNextMatch,
  onPreviousMatch,
  onClose,
}) => {
  const { t } = useTranslation('ui')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        onPreviousMatch()
      } else {
        onNextMatch()
      }
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  const displayCount =
    totalMatches > 0
      ? t('pdfViewer.search.resultsCount', {
          current: currentMatchIndex + 1,
          total: totalMatches,
        })
      : t('pdfViewer.search.noMatches')

  return (
    <SearchBarContainer>
      <SearchInput
        type="text"
        value={searchQuery}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={t('pdfViewer.toolbar.searchPlaceholder')}
        autoFocus
      />

      {searchQuery && (
        <>
          <SearchCounter>{displayCount}</SearchCounter>

          <SearchControls>
            <SearchButton
              onClick={onPreviousMatch}
              disabled={totalMatches === 0}
              title={t('pdfViewer.search.previousMatch')}
            >
              <Icon name="chevron-up" size="small" />
            </SearchButton>

            <SearchButton
              onClick={onNextMatch}
              disabled={totalMatches === 0}
              title={t('pdfViewer.search.nextMatch')}
            >
              <Icon name="chevron-down" size="small" />
            </SearchButton>
          </SearchControls>
        </>
      )}

      <CloseButton onClick={onClose} title={t('pdfViewer.search.closeSearch')}>
        <Icon name="x" size="small" />
      </CloseButton>
    </SearchBarContainer>
  )
}
